import { Component, Input, OnInit } from '@angular/core';
import { MemeTokenTransaction } from '@src/app/modules/meme-dashboard/meme-dashboard.type';
import { CommonService } from '@src/app/services/common.service';
import { WalletStatusService } from '@src/app/services/wallet-status.service';
import { Column } from '@src/app/shared/types/ss-table.type';
import { ChainEnum } from '@src/app/shared/types/wallet-chain.type';
import { getAge } from '@src/app/shared/utils/common.util';
import { formatDecimalPart } from '@src/app/shared/utils/number.util';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { MemeDashboardService } from '../../../../meme-dashboard.service';

type FilterType = 'all' | 'smart_buy' | 'smart_sell' | 'top_holders';
type TimeRange = '15m' | '1h' | '24h';

@Component({
    selector: 'app-highlighted-txns',
    templateUrl: './highlighted-txns.component.html',
    styleUrls: ['./highlighted-txns.component.less'],
})
export class HighlightedTxnsComponent implements OnInit {
    @Input() address: string;
    @Input() chainName: ChainEnum | 'sol' | 'monad_testnet';

    formatDecimalPart = formatDecimalPart;

    durations: TimeRange[] = ['15m', '1h', '24h'];

    total: number;
    txns: MemeTokenTransaction[];

    loading = false;

    columns: Column[] = [
        {
            id: 'trader',
            name: 'Trader',
            width: '240px',
        },

        {
            id: 'tx_type',
            name: 'Type',
            showFilter: true,
            filterType: 'string',
            quickOptions: [
                { name: 'Sell', value: 'sell', style: { color: '#FF462A' } },
                { name: 'Buy', value: 'buy', style: { color: '#12BE51' } },
            ],
            filterData: null,
        },

        {
            id: 'total_usd',
            name: 'Total USD',
        },

        {
            id: 'amount',
            name: 'Amount',
        },

        {
            id: 'price',
            name: 'Price',
        },
        {
            id: 'time',
            name: 'Time',
        },
    ];

    queryParams: NzTableQueryParams & {
        type: FilterType;
        timerange: TimeRange;
        last_block_timestamp?: string;
        searchedAddress?: string;
    } = {
        pageIndex: 1,
        pageSize: 10,
        sort: [],
        filter: [],
        type: 'all',
        timerange: '15m',
        last_block_timestamp: null,
        searchedAddress: '',
    };

    editingNoteAddresses: { address: string; label: string }[] = [];

    savingNotesAddresses: string[] = [];

    ageMap: Record<string, string> = {};

    get viewAllTxLink() {
        if (this.chainName === ChainEnum.BASE) {
            return `https://basescan.org/token/${this.address}`;
        }

        if (this.chainName === ChainEnum.BSC) {
            return `https://bscscan.com/token/${this.address}`;
        }

        return '';
    }

    constructor(public commonService: CommonService, private memeDashboardService: MemeDashboardService, private walletStatusService: WalletStatusService) {}

    ngOnInit() {
        this.fetchData();
    }

    changeFilterType(filter: FilterType) {
        if (filter === this.queryParams.type) return;

        this.total = 0;

        this.queryParams.type = filter;

        this.resetPageAndFetchData();
    }

    applyFilter(data: { column: Column; value: any }) {
        this.updateFilterParams(data);
        this.resetPageAndFetchData();
    }

    startEdit(item: any) {
        if (!this.walletStatusService.isWalletConnected) {
            this.commonService.showLogInModal();
            return;
        }
        this.editingNoteAddresses = uniqBy([...this.editingNoteAddresses, { address: item.address, label: item.note }], 'address');
    }

    stopEdit(address: string) {
        this.editingNoteAddresses = this.editingNoteAddresses.filter(item => address !== item.address);
    }

    getEditingNote(address: string): string {
        return this.editingNoteAddresses.find(item => item.address === address)?.label || '';
    }

    setEditingNote(address: string, note: string) {
        const index = this.editingNoteAddresses.findIndex(item => item.address === address);
        if (index > -1) {
            this.editingNoteAddresses[index].label = note;
        }
    }

    isEditing(address: string) {
        return this.editingNoteAddresses.some(item => item.address === address);
    }

    saveNotes(address: string) {
        if (this.savingNotesAddresses.includes(address)) return;

        this.savingNotesAddresses.push(address);
        const label = this.editingNoteAddresses.find(item => item.address === address)?.label;
        this.memeDashboardService
            .addLabel(address, label)
            .then(() => {
                this.editingNoteAddresses = this.editingNoteAddresses.filter(item => item.address !== address);
                this.txns.filter(item => item.address === address).forEach(item => (item.label = label));
            })
            .finally(() => {
                this.savingNotesAddresses = this.savingNotesAddresses.filter(item => item !== address);
            });
    }

    durationChange() {
        this.resetPageAndFetchData();
    }

    fetchData(append = false) {
        this.loading = true;
        this.memeDashboardService
            .getTokenTransactions(this.chainName, this.address, {
                timerange: this.queryParams.timerange,
                type: this.queryParams.type,
                last_block_timestamp: this.queryParams.last_block_timestamp,
                tx_type: this.queryParams.filter.find(item => item.key === 'tx_type')?.value ?? 'all',
            })
            .then(({ total, data, last_block_timestamp }) => {
                this.total = total;

                data.forEach(item => {
                    item.block_timestamp = dayjs.utc(item.block_timestamp).toString();
                });

                this.queryParams.last_block_timestamp = last_block_timestamp;

                this.txns = append ? [...this.txns, ...data] : data;
                data.map(item => (this.ageMap[item.block_timestamp] = getAge(item.block_timestamp)));
            })
            .catch(err => this.commonService.errorMessageByResponse(err))
            .finally(() => (this.loading = false));
    }

    /**
     * 辅助方法
     */
    private resetPageAndFetchData() {
        this.queryParams.last_block_timestamp = null;
        this.queryParams.pageIndex = 1;
        this.fetchData();
    }

    private updateFilterParams(data: { column: Column; value: any }) {
        this.queryParams.filter = uniqBy([{ key: data.column.id, value: data.value }, ...this.queryParams.filter], 'key');
    }
}
